import { Inject, Injectable, InjectionToken } from "@angular/core";
import PouchDB from "pouchdb";
import { from, Observable } from "rxjs";

export interface FormDraftKey {
  ownerUserId: number;
  companyId: number;
  formType: string;
  mode: "create" | "edit";
  contextId: string;
  sessionId?: string;
}

export interface FormDraftDocument<T = unknown> extends FormDraftKey {
  _id: string;
  _rev?: string;
  kind: "form-draft";
  schemaVersion: 1;
  formVersion: number;
  generation: number;
  status: "active" | "committed";
  payload: T;
  baselineFingerprint: string;
  createdAt: string;
  updatedAt: string;
  savedRecordId?: string;
}

export const FORM_DRAFT_DATABASE = new InjectionToken<PouchDB.Database>(
  "FORM_DRAFT_DATABASE",
  {
    providedIn: "root",
    factory: () =>
      new PouchDB("dive_ops_drafts.db", {
        revs_limit: 1,
        auto_compaction: true,
      }),
  }
);

@Injectable({ providedIn: "root" })
export class FormDraftService {
  private readonly restoreSuppressionPrefix = "FORM_DRAFT_COMMITTED:";
  private readonly inMemoryRestoreSuppressions = new Set<string>();

  constructor(
    @Inject(FORM_DRAFT_DATABASE)
    private readonly database: PouchDB.Database
  ) {}

  public buildId(key: FormDraftKey): string {
    const parts = [
      "form-draft",
      key.ownerUserId,
      key.companyId,
      key.formType,
      key.mode,
      key.contextId,
      key.sessionId,
    ].filter((part) => part !== undefined && part !== null && part !== "");
    return parts.map((part) => encodeURIComponent(String(part))).join(":");
  }

  public load<T>(key: FormDraftKey): Observable<FormDraftDocument<T> | null> {
    if (this.isRestoreSuppressed(key)) return from(Promise.resolve(null));
    return from(
      this.database
        .get(this.buildId(key))
        .then((document) => {
          const draft = document as unknown as FormDraftDocument<T>;
          this.assertOwner(draft, key);
          return draft.status === "active" ? draft : null;
        })
        .catch((error) => {
          if (error?.status === 404) return null;
          throw error;
        })
    );
  }

  public save<T>(
    key: FormDraftKey,
    payload: T,
    baselineFingerprint: string,
    formVersion = 1
  ): Observable<FormDraftDocument<T>> {
    return from(
      this.modify<T>(key, (current) => {
          const now = new Date().toISOString();
          return {
            ...current,
            _id: this.buildId(key),
            ...key,
            kind: "form-draft",
            schemaVersion: 1,
            formVersion,
            generation: (current?.generation ?? 0) + 1,
            status: "active",
            payload,
            baselineFingerprint,
            createdAt: current?.createdAt ?? now,
            updatedAt: now,
          };
        })
        .then((draft) => {
          this.clearRestoreSuppression(key);
          return draft;
        })
    );
  }

  public markCommitted(
    key: FormDraftKey,
    savedRecordId?: string
  ): Observable<FormDraftDocument | null> {
    return from(
      this.modify(key, (current) =>
        current
          ? {
              ...current,
              status: "committed",
              savedRecordId,
              generation: current.generation + 1,
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    );
  }

  public discard(key: FormDraftKey): Observable<boolean> {
    return from(
      this.database
        .get(this.buildId(key))
        .then((document) => {
          this.assertOwner(document as unknown as FormDraftDocument, key);
          return this.database.remove(document).then(() => {
            this.clearRestoreSuppression(key);
            return true;
          });
        })
        .catch((error) => {
          if (error?.status === 404) {
            this.clearRestoreSuppression(key);
            return false;
          }
          throw error;
        })
    );
  }

  public suppressRestore(key: FormDraftKey): boolean {
    const markerKey = this.restoreSuppressionKey(key);
    this.inMemoryRestoreSuppressions.add(markerKey);
    try {
      localStorage.setItem(markerKey, new Date().toISOString());
      return true;
    } catch (error) {
      console.error("Unable to persist form draft suppression", error);
      return false;
    }
  }

  private isRestoreSuppressed(key: FormDraftKey): boolean {
    const markerKey = this.restoreSuppressionKey(key);
    if (this.inMemoryRestoreSuppressions.has(markerKey)) return true;
    try {
      return localStorage.getItem(markerKey) !== null;
    } catch (error) {
      console.error("Unable to read form draft suppression", error);
      return true;
    }
  }

  private clearRestoreSuppression(key: FormDraftKey): void {
    const markerKey = this.restoreSuppressionKey(key);
    try {
      localStorage.removeItem(markerKey);
      this.inMemoryRestoreSuppressions.delete(markerKey);
    } catch (error) {
      console.error("Unable to clear form draft suppression", error);
    }
  }

  private restoreSuppressionKey(key: FormDraftKey): string {
    return `${this.restoreSuppressionPrefix}${this.buildId(key)}`;
  }

  private modify<T>(
    key: FormDraftKey,
    update: (
      current: FormDraftDocument<T> | null
    ) => FormDraftDocument<T>,
    conflictCount?: number
  ): Promise<FormDraftDocument<T>>;
  private modify<T>(
    key: FormDraftKey,
    update: (
      current: FormDraftDocument<T> | null
    ) => FormDraftDocument<T> | null,
    conflictCount?: number
  ): Promise<FormDraftDocument<T> | null>;
  private async modify<T>(
    key: FormDraftKey,
    update: (
      current: FormDraftDocument<T> | null
    ) => FormDraftDocument<T> | null,
    conflictCount = 0
  ): Promise<FormDraftDocument<T> | null> {
    let current: FormDraftDocument<T> | null = null;
    try {
      current = (await this.database.get(
        this.buildId(key)
      )) as unknown as FormDraftDocument<T>;
      this.assertOwner(current, key);
    } catch (error) {
      if (error?.status !== 404) throw error;
    }

    const updated = update(current);
    if (!updated) return null;
    try {
      const response = await this.database.put(updated);
      return {
        ...updated,
        _rev: response.rev,
      };
    } catch (error) {
      if (error?.status === 409 && conflictCount < 5) {
        return this.modify(key, update, conflictCount + 1);
      }
      throw error;
    }
  }

  private assertOwner(
    document: Pick<FormDraftDocument, "ownerUserId" | "companyId">,
    key: FormDraftKey
  ): void {
    if (
      document.ownerUserId !== key.ownerUserId ||
      document.companyId !== key.companyId
    ) {
      throw new Error("Draft owner does not match the active user and company");
    }
  }
}
